import { object, string } from 'yup'

/** Upper bound for a page number; guards against expanding absurd ranges */
export const maxPageNumber = 10000

const pageTokenPattern = /^(\d+)(?:\s*[-–]\s*(\d+))?$/

/**
 * Parses a page selection like "1-3, 5" into sorted, unique 1-based page numbers.
 * An empty selection returns null (= all pages), an invalid one undefined.
 */
export const parsePageSelection = (input: string): number[] | null | undefined => {
  const tokens = input
    .split(/[,;]/)
    .map(token => token.trim())
    .filter(token => token !== '')

  if (!tokens.length) return null

  const pages = new Set<number>()
  for (const token of tokens) {
    const match = pageTokenPattern.exec(token)
    if (!match) return undefined

    const first = Number(match[1])
    const last = match[2] === undefined ? first : Number(match[2])
    if (first < 1 || last < first || last > maxPageNumber) return undefined

    for (let page = first; page <= last; page++) pages.add(page)
  }

  return [...pages].sort((a, b) => a - b)
}

/** Formats page numbers as a compact selection, e.g. [1, 2, 3, 5] -> "1-3, 5". */
export const formatPageSelection = (pages?: number[] | null): string => {
  if (!pages?.length) return ''

  const runs: [number, number][] = []
  for (const page of [...new Set(pages)].sort((a, b) => a - b)) {
    const lastRun = runs[runs.length - 1]
    if (lastRun && lastRun[1] === page - 1) lastRun[1] = page
    else runs.push([page, page])
  }

  return runs.map(([first, last]) => (first === last ? `${first}` : `${first}-${last}`)).join(', ')
}

/** Pages of the document that the selection leaves out; none without a selection */
export const getExcludedPages = (pageCount: number, pages?: number[] | null): number[] => {
  if (!pages?.length) return []

  const selected = new Set(pages)
  return Array.from({ length: pageCount }, (_, index) => index + 1).filter(
    page => !selected.has(page),
  )
}

/** Selected pages beyond the document's `pageCount`; none while the count is unknown */
export const getMissingPages = (pageCount?: number, pages?: number[] | null): number[] =>
  pageCount && pages ? pages.filter(page => page > pageCount) : []

/**
 * Validates the selection text; pages beyond `pageCount` (once known) are rejected.
 * The already saved selection is exempt, so a stale one never blocks saving other fields.
 */
export const createPageSelectionSchema = (pageCount?: number, savedSelection?: string) =>
  object({
    page_selection: string()
      .test(
        'page-selection',
        'Ungültige Seitenauswahl, z. B. "1-3, 5"',
        value => parsePageSelection(value ?? '') !== undefined,
      )
      .test('page-selection-range', (value, context) => {
        if (value === savedSelection) return true

        const missingPages = getMissingPages(pageCount, parsePageSelection(value ?? ''))
        return missingPages.length
          ? context.createError({ message: `Seite ${missingPages.join(', ')} existiert nicht` })
          : true
      }),
  })

/** Replaces the form-only `page_selection` text by the stored `pages` list. */
export const withParsedPages = <T extends { page_selection: string }>({
  page_selection,
  ...config
}: T): Omit<T, 'page_selection'> & { pages: number[] | null } => ({
  ...config,
  pages: parsePageSelection(page_selection) ?? null,
})
